<template>
  <el-empty v-if="!groups.length" :description="$t('locationGroups.empty')" />
  <ul v-else class="location-groups-list">
    <li v-for="group in groups" :key="group.id" class="location-groups-list__item">
      <div>
        <strong>{{ group.name }}</strong>
        <div class="location-groups-list__counts">
          {{ $t('locationGroups.availableCount', { count: count(group, 'AVAILABLE') }) }} ·
          {{ $t('locationGroups.unavailableCount', { count: count(group, 'UNAVAILABLE') }) }}
        </div>
        <small>{{ group.createdBy }} · {{ formatDate(group.createdAt) }}</small>
      </div>
      <div class="location-groups-list__actions">
        <el-button :aria-label="$t('locationGroups.editAria', { name: group.name })" @click="emit('edit', group)">
          <el-icon><Edit /></el-icon>
        </el-button>
        <el-button
          type="danger"
          :aria-label="$t('locationGroups.deleteAria', { name: group.name })"
          @click="emit('remove', group)"
        >
          <el-icon><Delete /></el-icon>
        </el-button>
      </div>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { Delete, Edit } from '@element-plus/icons-vue'

import type { GeolocationAvailabilityType } from '@/types/GeolocationModel'

import type { LocationGroup } from '../domain/locationGroup'

defineProps<{ groups: readonly LocationGroup[] }>()

const emit = defineEmits<{
  edit: [group: LocationGroup]
  remove: [group: LocationGroup]
}>()

function count(group: LocationGroup, type: GeolocationAvailabilityType): number {
  return group.geolocations.filter(location => location.availabilityType === type).length
}

function formatDate(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date)
}
</script>

<style scoped lang="scss">
.location-groups-list {
  display: grid;
  gap: 8px;
  padding: 0;
  list-style: none;

  &__item,
  &__actions {
    display: flex;
    align-items: center;
  }

  &__item {
    justify-content: space-between;
    gap: 16px;
    padding: 12px;
    border: 1px solid var(--el-border-color-light);
    border-radius: var(--el-border-radius-base);
  }

  &__counts,
  small {
    color: var(--el-text-color-secondary);
  }

  &__actions {
    gap: 4px;
  }
}
</style>
